import { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Stack,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshButton from '../components/RefreshButton';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import CloseIcon from '@mui/icons-material/Close';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LockResetIcon from '@mui/icons-material/LockReset';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import TuneIcon from '@mui/icons-material/Tune';
import { userService, type ListUsersParams, type CreateUserRequest, type UpdateUserRequest } from '../services/user.service';
import { groupService } from '../services/group.service';
import type { User, UserOption, Group, UserRole } from '@/types/user.types';
import { useAuthStore } from '@/stores/authStore';
import { emailDomainProblem, emailDomainsText, emailProblem, normalizeUsername, USERNAME_RULE_TEXT, usernameProblem } from '@/utils/accountRules';
import { useFieldErrors, type FieldErrors } from '@/utils/formErrors';
import { usePasswordPolicy } from '@/hooks/usePasswordPolicy';
import { useEmailDomainPolicy } from '@/hooks/useAuthConfig';
import { settingsService } from '../services/settings.service';
import type { PasswordPolicySetting } from '@/types/settings.types';

// QA #68: which input a refusal belongs to. Domain codes → field; the
// backend's primary_group_id is chosen through the 群組指派 select here.
const CREATE_USER_FIELDS: Record<string, string> = {
  USERNAME_INVALID: 'username',
  USERNAME_EXISTS: 'username',
  EMAIL_EXISTS: 'email',
  USER_EXISTS: 'email',
  primary_group_id: 'group_ids',
};
const EDIT_USER_FIELDS: Record<string, string> = {
  EMAIL_EXISTS: 'email',
  USER_EXISTS: 'email',
  primary_group_id: 'group_ids',
};

const ROLE_LABELS: Record<UserRole, string> = {
  admin: '最高管理員',
  sub_admin: '群組管理員',
  operator: '操作員',
};

const ROLE_COLORS: Record<UserRole, 'error' | 'warning' | 'info'> = {
  admin: 'error',
  sub_admin: 'warning',
  operator: 'info',
};

// 密碼到期 column: a password this close to expiry is flagged.
const EXPIRY_WARNING_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString('zh-TW') : '-');

export default function Users() {
  const { user: currentUser, isSubAdmin } = useAuthStore();
  // QA #67: the deployment's PASSWORD_MAX_AGE_DAYS (GET /auth/config) —
  // the fallback until GET /settings/password-policy answers.
  const authPolicy = usePasswordPolicy();
  // QA #91: the deployment may restrict which e-mail domains accounts use.
  const { userDomains } = useEmailDomainPolicy();
  const emailHint = userDomains.length > 0 ? `僅允許使用 ${emailDomainsText(userDomains)} 的信箱` : undefined;
  // ADR-049: the value in force (console setting or deployment default).
  const [policySetting, setPolicySetting] = useState<PasswordPolicySetting | null>(null);
  const maxAgeDays = policySetting?.max_age_days ?? authPolicy.maxAgeDays;

  // User list state
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  // QA #75: the account was created but its 設定密碼 invite did not go out;
  // the banner says why and offers 寄送設定密碼信 for that user.
  const [inviteFailure, setInviteFailure] = useState<{ user: User; reason: string } | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalUsers, setTotalUsers] = useState(0);

  // Search & filters
  const [searchKeyword, setSearchKeyword] = useState('');
  const [roleFilter] = useState<string>('');
  const [activeFilter] = useState<string>('');

  // QA #76: 「快速找使用者」dropdown, filtered server-side by GET /users/options.
  const [quickFindOpen, setQuickFindOpen] = useState(false);
  const [quickFindQuery, setQuickFindQuery] = useState('');
  const [quickFindOptions, setQuickFindOptions] = useState<UserOption[]>([]);
  const [quickFindLoading, setQuickFindLoading] = useState(false);
  const [quickFindValue, setQuickFindValue] = useState<UserOption | null>(null);

  // Groups
  const [groups, setGroups] = useState<Group[]>([]);

  // Create dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    username: '',
    email: '',
    role: 'operator',
    group_ids: [],
    primary_group_id: '',
  });
  // createError / editError are for problems that belong to no field; field
  // problems are marked on the input (QA #68).
  const [createError, setCreateError] = useState('');
  const createFields = useFieldErrors();

  // Edit dialog (username is read-only: backend UpdateUserRequest has no username field)
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserRequest>({
    email: '',
    role: 'operator',
    group_ids: [],
  });
  const [editError, setEditError] = useState('');
  const editFields = useFieldErrors();

  // 寄送設定密碼信 dialog (QA #75: replaces the admin password reset)
  const [setupMailDialogOpen, setSetupMailDialogOpen] = useState(false);
  const [sendingSetupMail, setSendingSetupMail] = useState(false);
  const [setupMailUser, setSetupMailUser] = useState<User | null>(null);
  const [setupMailError, setSetupMailError] = useState('');

  // 調整密碼有效期限 dialog (ADR-049, admin)
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);
  const [policyDays, setPolicyDays] = useState('');
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState('');
  const policyFields = useFieldErrors();

  // 要求下次登入更換密碼 dialog (must_change_password)
  const [requireChangeUser, setRequireChangeUser] = useState<User | null>(null);
  const [requiringChange, setRequiringChange] = useState(false);
  const [requireChangeError, setRequireChangeError] = useState('');

  // 解除鎖定 dialog (ADR-055, QA #106)
  const [unlockUser, setUnlockUser] = useState<User | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState('');

  // Toggle active state
  const [toggling, setToggling] = useState<string | null>(null);

  // Permanent delete dialog (admin only; the account must be deactivated first)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteBlockers, setDeleteBlockers] = useState<Record<string, number> | null>(null);

  useEffect(() => {
    loadUsers();
    loadGroups();
  }, [page, rowsPerPage, roleFilter, activeFilter]);

  // ADR-049: the policy line and the 調整 dialog read the value in force.
  useEffect(() => {
    settingsService
      .getPasswordPolicy()
      .then(setPolicySetting)
      .catch((err) => console.error('Failed to load password policy:', err));
  }, []);

  // searchOverride: the keyword a handler has just set (state is stale inside
  // that same handler); otherwise the search box's current value.
  const loadUsers = async (searchOverride?: string) => {
    try {
      setLoading(true);
      setError('');
      const params: ListUsersParams = {
        page: page + 1,
        page_size: rowsPerPage,
        search: (searchOverride ?? searchKeyword) || undefined,
        role: roleFilter || undefined,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
      };
      const result = await userService.listUsers(params);
      setUsers(result?.items ?? []);
      setTotalUsers(result?.pagination?.total ?? 0);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('載入使用者列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    // sub_admin lacks permission to call /api/v1/groups; fall back to their
    // own group memberships which are already embedded in the auth token.
    if (isSubAdmin() && currentUser?.groups) {
      setGroups(currentUser.groups);
      return;
    }
    try {
      const data = await groupService.listGroups();
      setGroups(data);
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  };

  const handleSearch = () => {
    setPage(0);
    loadUsers();
  };

  // QA #76: while the dropdown is open, fetch its options 300 ms after the
  // last keystroke; a response for an older query is dropped.
  useEffect(() => {
    if (!quickFindOpen) return;
    let active = true;
    const timer = setTimeout(async () => {
      setQuickFindLoading(true);
      try {
        const options = await userService.getOptions({ search: quickFindQuery || undefined, limit: 20 });
        if (active) setQuickFindOptions(options);
      } catch (err) {
        console.error('Failed to load user options:', err);
      } finally {
        if (active) setQuickFindLoading(false);
      }
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [quickFindOpen, quickFindQuery]);

  // Picking an option shows that user in the table (same server-side search
  // as the box, back on the first page); clearing the dropdown clears it.
  const handleQuickFind = (option: UserOption | null) => {
    setQuickFindValue(option);
    const username = option?.username ?? '';
    setSearchKeyword(username);
    if (page === 0) {
      loadUsers(username);
    } else {
      setPage(0); // the pagination effect reloads with the new keyword
    }
  };

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'username',
      headerName: '使用者名稱',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>{params.value}</Typography>
      ),
    },
    { field: 'email', headerName: '電子信箱', flex: 1.2, minWidth: 160 },
    {
      field: 'role',
      headerName: '角色',
      width: 110,
      type: 'singleSelect',
      valueOptions: Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label })),
      renderCell: (params) => (
        <Chip
          label={ROLE_LABELS[params.value as UserRole] || params.value}
          color={ROLE_COLORS[params.value as UserRole] || 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'groups',
      headerName: '群組',
      flex: 1,
      minWidth: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const user = params.row as User;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ py: 0.5 }}>
            {user.groups && user.groups.length > 0 ? (
              user.groups.map((group) => (
                <Chip
                  key={group.id}
                  label={group.name}
                  size="small"
                  variant={user.primary_group?.id === group.id ? 'filled' : 'outlined'}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">-</Typography>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'is_active',
      headerName: '狀態',
      width: 170,
      type: 'boolean',
      renderCell: (params) => {
        const user = params.row as User;
        const lockedUntil = lockedUntilOf(user);
        return (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
            <Chip
              label={params.value ? '啟用' : '停用'}
              color={params.value ? 'success' : 'default'}
              size="small"
            />
            {lockedUntil && (
              <Tooltip title="連續輸錯密碼，暫時無法登入（ADR-055）">
                <Chip
                  label={`已鎖定（至 ${lockedUntil.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })}）`}
                  color="error"
                  size="small"
                  variant="outlined"
                  data-testid="user-locked-chip"
                />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'last_login_at',
      headerName: '最後登入',
      width: 160,
      valueFormatter: (value: string | null) =>
        value ? new Date(value).toLocaleString('zh-TW') : '-',
    },
    {
      field: 'password_expires_at',
      headerName: '密碼到期',
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const user = params.row as User;
        const changed = `上次更換：${formatDate(user.password_changed_at)}`;
        if (user.must_change_password) {
          return (
            <Tooltip title={`${user.password_expired ? '密碼已過期。' : ''}下次登入必須先設定新密碼（${changed}）`}>
              <Chip label="下次登入須更換" color="warning" size="small" />
            </Tooltip>
          );
        }
        if (user.password_expired) {
          return (
            <Tooltip title={`密碼已超過 ${maxAgeDays} 天未更換，下次登入必須先設定新密碼（${changed}）`}>
              <Chip label="已過期" color="error" size="small" />
            </Tooltip>
          );
        }
        if (!user.password_expires_at) {
          return (
            <Tooltip title={changed}>
              <Typography variant="body2" color="text.secondary">不到期</Typography>
            </Tooltip>
          );
        }
        const daysLeft = Math.max(0, Math.ceil((new Date(user.password_expires_at).getTime() - Date.now()) / DAY_MS));
        const title = `到期日：${formatDate(user.password_expires_at)}（${changed}）`;
        return (
          <Tooltip title={title}>
            {daysLeft <= EXPIRY_WARNING_DAYS ? (
              <Chip label={`${daysLeft} 天後到期`} color="warning" variant="outlined" size="small" />
            ) : (
              <Typography variant="body2">{daysLeft} 天後到期</Typography>
            )}
          </Tooltip>
        );
      },
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 170,
      sortable: false,
      filterable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => {
        const user = params.row as User;
        return (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Tooltip title="編輯">
              <IconButton size="small" color="primary" onClick={() => openEditDialog(user)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={user.is_active ? '停用帳號' : '啟用帳號'}>
              <span>
                <IconButton
                  size="small"
                  color={user.is_active ? 'warning' : 'success'}
                  onClick={() => handleToggleActive(user)}
                  disabled={toggling === user.id || user.id === currentUser?.id}
                >
                  {toggling === user.id ? (
                    <CircularProgress size={18} />
                  ) : (
                    <PersonOffIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="寄送設定密碼信">
              <IconButton size="small" color="secondary" onClick={() => openSetupMailDialog(user)}>
                <ForwardToInboxIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {lockedUntilOf(user) && (
              <Tooltip title={unlockBlocker(user) ?? '解除鎖定'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="解除鎖定"
                    onClick={() => openUnlockDialog(user)}
                    disabled={unlockBlocker(user) !== null}
                  >
                    <LockOpenIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={requireChangeBlocker(user) ?? '要求下次登入更換密碼'}>
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => openRequireChangeDialog(user)}
                  disabled={requireChangeBlocker(user) !== null}
                >
                  <LockResetIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {!isSubAdmin() && (
              <Tooltip title={user.is_active ? '永久刪除（請先停用帳號）' : '永久刪除'}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => openDeleteDialog(user)}
                    disabled={user.is_active || user.id === currentUser?.id}
                  >
                    <DeleteForeverIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
  ], [toggling, currentUser, maxAgeDays]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Create User ---

  const openCreateDialog = () => {
    setCreateForm({
      username: '',
      email: '',
      role: isSubAdmin() ? 'operator' : 'operator',
      group_ids: [],
      primary_group_id: '',
    });
    setCreateError('');
    createFields.clear();
    setCreateDialogOpen(true);
  };

  const handleCreateUser = async () => {
    // QA #68: every problem is marked on its own field at once.
    const problems: FieldErrors = {};
    if (!createForm.username.trim()) {
      problems.username = `請輸入使用者名稱（${USERNAME_RULE_TEXT}）`;
    } else {
      // QA #71: the account-name rule (backend internal/users/username.go)
      const usernameIssue = usernameProblem(normalizeUsername(createForm.username));
      if (usernameIssue) problems.username = usernameIssue;
    }
    const emailIssue = emailProblem(createForm.email) || emailDomainProblem(createForm.email, userDomains);
    if (emailIssue) problems.email = emailIssue;
    // Backend CreateUserRequest: group_ids required,min=1 and primary_group_id required
    if (createForm.group_ids.length === 0 || !createForm.primary_group_id) {
      problems.group_ids = '請選擇至少一個群組（第一個選到的是主要群組）';
    }
    if (Object.keys(problems).length > 0) {
      createFields.setErrors(problems);
      return;
    }

    try {
      setCreating(true);
      setCreateError('');
      createFields.clear();
      // QA #75: no password — the backend mails the user a 設定密碼 link.
      const created = await userService.createUser(createForm);
      setCreateDialogOpen(false);
      if (created.invite_sent) {
        setInviteFailure(null);
        setSuccessMsg(`使用者「${created.username}」已建立，已寄出設定密碼信到 ${created.email}`);
      } else {
        // The account exists; only the mail failed. Offer a resend.
        setSuccessMsg('');
        setInviteFailure({
          user: created,
          reason: created.invite_error || '設定密碼信寄送失敗',
        });
      }
      loadUsers();
    } catch (err) {
      console.error('Failed to create user:', err);
      // VALIDATION_ERROR fields / USERNAME_* / EMAIL_EXISTS land on the field.
      setCreateError(createFields.apply(err, CREATE_USER_FIELDS, '建立使用者失敗，請稍後再試'));
    } finally {
      setCreating(false);
    }
  };

  // --- Edit User ---

  const openEditDialog = (user: User) => {
    setEditingUser(user);
    setEditForm({
      email: user.email,
      role: user.role,
      group_ids: user.groups?.map((g) => g.id) || [],
    });
    setEditError('');
    editFields.clear();
    setEditDialogOpen(true);
  };

  const handleEditUser = async () => {
    if (!editingUser) return;
    // QA #91: only a changed address must be in the allowed domains (the
    // backend checks the same), so an older account stays editable.
    const changed = (editForm.email ?? '').trim().toLowerCase() !== editingUser.email.trim().toLowerCase();
    const emailIssue =
      emailProblem(editForm.email ?? '') || (changed ? emailDomainProblem(editForm.email ?? '', userDomains) : '');
    if (emailIssue) {
      editFields.setErrors({ email: emailIssue });
      return;
    }

    try {
      setEditing(true);
      setEditError('');
      editFields.clear();
      // A sub_admin cannot change roles; the form always echoes the current
      // role, so drop it from the payload to avoid a 403 (QA #3). The backend
      // also tolerates an unchanged role, this just keeps the request clean.
      const payload: UpdateUserRequest = { ...editForm };
      if (isSubAdmin()) delete payload.role;
      await userService.updateUser(editingUser.id, payload);
      setEditDialogOpen(false);
      setSuccessMsg('使用者已更新');
      loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      setEditError(editFields.apply(err, EDIT_USER_FIELDS, '更新使用者失敗，請稍後再試'));
    } finally {
      setEditing(false);
    }
  };

  // --- Toggle Active ---

  const handleToggleActive = async (user: User) => {
    try {
      setToggling(user.id);
      await userService.updateUser(user.id, { is_active: !user.is_active });
      setSuccessMsg(`使用者「${user.username}」已${user.is_active ? '停用' : '啟用'}`);
      loadUsers();
    } catch (err) {
      console.error('Failed to toggle user active:', err);
      setError('切換使用者狀態失敗');
    } finally {
      setToggling(null);
    }
  };

  // --- 寄送設定密碼信 (QA #75) ---

  const openSetupMailDialog = (user: User) => {
    setSetupMailUser(user);
    setSetupMailError('');
    setSetupMailDialogOpen(true);
  };

  const handleSendSetupMail = async () => {
    if (!setupMailUser) return;
    try {
      setSendingSetupMail(true);
      setSetupMailError('');
      await userService.sendPasswordSetupEmail(setupMailUser.id);
      setSetupMailDialogOpen(false);
      if (inviteFailure?.user.id === setupMailUser.id) setInviteFailure(null);
      setSuccessMsg(`已寄出設定密碼信給「${setupMailUser.username}」（${setupMailUser.email}）`);
    } catch (err) {
      console.error('Failed to send password setup email:', err);
      // skipGlobalError: the backend's Chinese reason (SMTP_UNAVAILABLE /
      // CONSOLE_URL_UNSET) is shown here.
      setSetupMailError(err instanceof Error && err.message ? err.message : '寄送設定密碼信失敗');
    } finally {
      setSendingSetupMail(false);
    }
  };

  // --- 調整密碼有效期限 (ADR-049) ---

  const policyRange = { min: policySetting?.min_days ?? 30, max: policySetting?.max_days ?? 90 };
  const policyRangeText = `請輸入 ${policyRange.min}～${policyRange.max} 的天數`;
  const deploymentDefaultText =
    (policySetting?.deployment_default ?? 0) > 0 ? `${policySetting?.deployment_default} 天` : '不到期';

  const openPolicyDialog = () => {
    setPolicyDays(maxAgeDays > 0 ? String(maxAgeDays) : '');
    setPolicyError('');
    policyFields.clear();
    setPolicyDialogOpen(true);
  };

  const handleSavePolicy = async () => {
    const days = Number(policyDays);
    if (!/^\d+$/.test(policyDays.trim()) || days < policyRange.min || days > policyRange.max) {
      policyFields.setErrors({ max_age_days: policyRangeText });
      return;
    }
    try {
      setPolicySaving(true);
      setPolicyError('');
      policyFields.clear();
      const updated = await settingsService.updatePasswordPolicy(days);
      setPolicySetting(updated);
      setPolicyDialogOpen(false);
      setSuccessMsg(`密碼有效期限已改為 ${updated.max_age_days} 天`);
      loadUsers(); // the 密碼到期 column is computed server-side
    } catch (err) {
      console.error('Failed to update password policy:', err);
      setPolicyError(policyFields.apply(err, {}, '儲存失敗，請稍後再試'));
    } finally {
      setPolicySaving(false);
    }
  };

  const handleResetPolicy = async () => {
    try {
      setPolicySaving(true);
      setPolicyError('');
      const updated = await settingsService.resetPasswordPolicy();
      setPolicySetting(updated);
      setPolicyDialogOpen(false);
      setSuccessMsg(
        updated.max_age_days > 0
          ? `密碼有效期限已還原為部署預設 ${updated.max_age_days} 天`
          : '密碼有效期限已還原為部署預設（不到期）',
      );
      loadUsers();
    } catch (err) {
      console.error('Failed to reset password policy:', err);
      setPolicyError('還原預設失敗，請稍後再試');
    } finally {
      setPolicySaving(false);
    }
  };

  // --- 解除鎖定 (ADR-055, QA #106) ---

  // When the account is locked, the end of the lock; otherwise null.
  const lockedUntilOf = (user: User): Date | null => {
    if (!user.locked_until) return null;
    const until = new Date(user.locked_until);
    return until.getTime() > Date.now() ? until : null;
  };

  // Same reach as 要求下次登入更換密碼: a group manager only for operators.
  const unlockBlocker = (user: User): string | null => {
    if (isSubAdmin() && user.role !== 'operator') return '群組管理員只能解除操作員的鎖定';
    return null;
  };

  const openUnlockDialog = (user: User) => {
    setUnlockUser(user);
    setUnlockError('');
  };

  const handleUnlock = async () => {
    if (!unlockUser) return;
    try {
      setUnlocking(true);
      setUnlockError('');
      await userService.unlock(unlockUser.id);
      setSuccessMsg(`已解除「${unlockUser.username}」的登入鎖定`);
      setUnlockUser(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to unlock user:', err);
      setUnlockError('解除鎖定失敗，請稍後再試');
    } finally {
      setUnlocking(false);
    }
  };

  // --- 要求下次登入更換密碼 ---

  // Why the button is off for this row, or null when it can be used. The
  // backend lets a group manager (sub_admin) do this to operators only.
  const requireChangeBlocker = (user: User): string | null => {
    if (user.id === currentUser?.id) return '要更換自己的密碼請到「密碼修改」頁';
    if (user.must_change_password) return '已要求更換，等待使用者下次登入';
    if (isSubAdmin() && user.role !== 'operator') return '群組管理員只能對操作員要求更換密碼';
    return null;
  };

  const openRequireChangeDialog = (user: User) => {
    setRequireChangeUser(user);
    setRequireChangeError('');
  };

  const handleRequireChange = async () => {
    if (!requireChangeUser) return;
    try {
      setRequiringChange(true);
      setRequireChangeError('');
      await userService.requirePasswordChange(requireChangeUser.id);
      setSuccessMsg(`已要求「${requireChangeUser.username}」下次登入時更換密碼`);
      setRequireChangeUser(null);
      loadUsers();
    } catch (err) {
      console.error('Failed to require password change:', err);
      setRequireChangeError('設定失敗，請稍後再試');
    } finally {
      setRequiringChange(false);
    }
  };

  // --- Permanent delete ---
  const openDeleteDialog = (user: User) => {
    setDeleteUser(user);
    setDeleteError('');
    setDeleteBlockers(null);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!deleteUser) return;
    try {
      setDeleting(true);
      setDeleteError('');
      setDeleteBlockers(null);
      await userService.deleteUserPermanently(deleteUser.id);
      setDeleteDialogOpen(false);
      setSuccessMsg(`使用者「${deleteUser.username}」已永久刪除`);
      loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      const e = err as { code?: string; details?: Record<string, number>; message?: string };
      if (e.code === 'USER_HAS_RESOURCES' && e.details) {
        setDeleteBlockers(e.details);
      }
      setDeleteError(e.message || '刪除使用者失敗');
    } finally {
      setDeleting(false);
    }
  };

  // --- Helpers ---

  const getAvailableRoles = (): UserRole[] => {
    if (isSubAdmin()) {
      return ['operator'];
    }
    return ['admin', 'sub_admin', 'operator'];
  };


  // QA #97: a disabled group takes no new members, so it is not offered.
  // `keep` are groups the user already belongs to (the edit form): they stay
  // selectable so the membership can still be removed.
  const getAvailableGroups = (keep: string[] = []): Group[] => {
    let available = groups;
    if (isSubAdmin() && currentUser) {
      // Sub-admin can only assign to their own groups
      const myGroupIds = currentUser.groups?.map((g) => g.id) || [];
      available = groups.filter((g) => myGroupIds.includes(g.id));
    }
    return available.filter((g) => g.is_active !== false || keep.includes(g.id));
  };

  const groupLabel = (group: Group): string => (group.is_active === false ? `${group.name}（已停用）` : group.name);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            使用者管理
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            管理系統使用者帳號
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PersonAddIcon />}
          onClick={openCreateDialog}
          size="large"
        >
          新增使用者
        </Button>
      </Box>

      {/* Success / Error alerts */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {inviteFailure && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button color="inherit" size="small" onClick={() => openSetupMailDialog(inviteFailure.user)}>
                寄送設定密碼信
              </Button>
              <IconButton size="small" color="inherit" onClick={() => setInviteFailure(null)} aria-label="關閉">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          使用者「{inviteFailure.user.username}」已建立，但設定密碼信寄送失敗：{inviteFailure.reason}
        </Alert>
      )}

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6">
          使用者列表
        </Typography>
        {/* ADR-049: the value in force, where it comes from, and (admin) the 調整 button */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
          <Typography variant="body2" color="text.secondary">
            {maxAgeDays > 0
              ? `密碼有效期限 ${maxAgeDays} 天：超過期限未更換的使用者，下次登入必須先設定新密碼。`
              : '密碼不會過期（有效期限未啟用）。'}
            {policySetting?.source === 'console'
              ? `主控台設定${
                  policySetting.updated_by
                    ? `，由 ${policySetting.updated_by.username} 於 ${formatDate(policySetting.updated_at)} 修改`
                    : ''
                }；部署預設 ${deploymentDefaultText}。`
              : '目前為部署預設。'}
          </Typography>
          {!isSubAdmin() && (
            <Button size="small" variant="outlined" startIcon={<TuneIcon />} onClick={openPolicyDialog} disabled={!policySetting}>
              調整
            </Button>
          )}
        </Stack>

        {/* 搜尋列 */}
        <Stack direction="row" spacing={2} mb={2} alignItems="center">
          <TextField
            size="small"
            placeholder="搜尋使用者名稱或信箱..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            sx={{ minWidth: 300 }}
          />
          <Button variant="contained" onClick={handleSearch}>
            搜尋
          </Button>
          <Autocomplete
            size="small"
            sx={{ minWidth: 300 }}
            open={quickFindOpen}
            onOpen={() => setQuickFindOpen(true)}
            onClose={() => setQuickFindOpen(false)}
            options={quickFindOptions}
            value={quickFindValue}
            onChange={(_, option) => handleQuickFind(option)}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') setQuickFindQuery(value);
            }}
            filterOptions={(options) => options}
            getOptionLabel={(option) => `${option.username}（${option.email}）`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            loading={quickFindLoading}
            loadingText="搜尋中..."
            noOptionsText="沒有符合的使用者"
            renderInput={(params) => <TextField {...params} label="快速找使用者" />}
          />
          <RefreshButton onClick={() => loadUsers()} loading={loading} />
        </Stack>

        {/* User DataGrid */}
        <DataGrid
          rows={users}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={totalUsers}
          paginationModel={{ page, pageSize: rowsPerPage }}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          autoHeight
          localeText={{
            noRowsLabel: '暫無使用者資料',
          }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{ border: 'none', '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' } }}
        />
      </Paper>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonAddIcon color="primary" />
            <Typography variant="h6">新增使用者</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {createError && (
              <Alert severity="error">{createError}</Alert>
            )}
            {/* QA #80 (B): MFA defaults to on for new accounts */}
            <Alert severity="info">
              新帳號預設啟用 MFA（信箱驗證碼）：首次登入會寄驗證碼到下面填的信箱，請確認信箱收得到信；使用者之後可在「密碼修改」頁自行關閉。
            </Alert>

            <TextField
              fullWidth
              label="使用者名稱"
              value={createForm.username}
              // QA #71: stored lower-cased, so show it that way as they type
              onChange={(e) => setCreateForm({ ...createForm, username: e.target.value.toLowerCase() })}
              required
              {...createFields.bind('username', USERNAME_RULE_TEXT)}
            />

            <TextField
              fullWidth
              label="電子信箱"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              required
              {...createFields.bind('email', emailHint)}
            />

            {/* QA #75: no password here — the user sets their own through the mailed link. */}
            <Alert severity="info">
              系統會寄一封「設定密碼」信到使用者的信箱（24 小時內有效），管理員不會看到密碼
            </Alert>

            <FormControl fullWidth error={Boolean(createFields.errors.role)}>
              <InputLabel>角色</InputLabel>
              <Select
                name="role"
                value={createForm.role}
                label="角色"
                onChange={(e) => {
                  setCreateForm({ ...createForm, role: e.target.value });
                  createFields.clearField('role');
                }}
              >
                {getAvailableRoles().map((role) => (
                  <MenuItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </MenuItem>
                ))}
              </Select>
              {createFields.errors.role && <FormHelperText>{createFields.errors.role}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth required error={Boolean(createFields.errors.group_ids)}>
              <InputLabel>群組指派</InputLabel>
              <Select
                multiple
                name="group_ids"
                value={createForm.group_ids || []}
                label="群組指派"
                onChange={(e) => {
                  const value = e.target.value;
                  const ids = typeof value === 'string' ? value.split(',') : value;
                  setCreateForm({
                    ...createForm,
                    group_ids: ids,
                    primary_group_id: ids[0] ?? '',
                  });
                  createFields.clearField('group_ids');
                }}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(selected as string[]).map((id) => {
                      const group = groups.find((g) => g.id === id);
                      return (
                        <Chip key={id} label={group ? groupLabel(group) : id} size="small" />
                      );
                    })}
                  </Stack>
                )}
              >
                {getAvailableGroups().map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
              {createFields.errors.group_ids && <FormHelperText>{createFields.errors.group_ids}</FormHelperText>}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={16} /> : <AddIcon />}
          >
            {creating ? '建立中...' : '建立使用者'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !editing && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditIcon color="primary" />
            <Typography variant="h6">編輯使用者</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {editError && (
              <Alert severity="error">{editError}</Alert>
            )}

            <TextField
              fullWidth
              label="使用者名稱"
              value={editingUser?.username || ''}
              disabled
              helperText="使用者名稱建立後無法修改"
            />

            <TextField
              fullWidth
              label="電子信箱"
              type="email"
              value={editForm.email || ''}
              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              required
              {...editFields.bind('email', emailHint)}
            />

            <FormControl fullWidth error={Boolean(editFields.errors.role)}>
              <InputLabel>角色</InputLabel>
              <Select
                name="role"
                value={editForm.role || 'operator'}
                label="角色"
                onChange={(e) => {
                  setEditForm({ ...editForm, role: e.target.value });
                  editFields.clearField('role');
                }}
                disabled={isSubAdmin()}
              >
                {getAvailableRoles().map((role) => (
                  <MenuItem key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </MenuItem>
                ))}
              </Select>
              {editFields.errors.role ? (
                <FormHelperText>{editFields.errors.role}</FormHelperText>
              ) : (
                isSubAdmin() && <FormHelperText>群組管理員僅能管理操作員，角色不可變更</FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth error={Boolean(editFields.errors.group_ids)}>
              <InputLabel>群組指派</InputLabel>
              <Select
                multiple
                name="group_ids"
                value={editForm.group_ids || []}
                label="群組指派"
                onChange={(e) => {
                  const value = e.target.value;
                  setEditForm({
                    ...editForm,
                    group_ids: typeof value === 'string' ? value.split(',') : value,
                  });
                  editFields.clearField('group_ids');
                }}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(selected as string[]).map((id) => {
                      const group = groups.find((g) => g.id === id);
                      return (
                        <Chip key={id} label={group ? groupLabel(group) : id} size="small" />
                      );
                    })}
                  </Stack>
                )}
              >
                {getAvailableGroups(editingUser?.groups?.map((g) => g.id) ?? []).map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {groupLabel(group)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {editFields.errors.group_ids ??
                  '移出某個群組時，該使用者在那個群組的檔案、File Watcher、排程與備份工作會一併移到他的主要群組。'}
              </FormHelperText>
            </FormControl>

            {editingUser && (
              <Typography variant="body2" color="text.secondary">
                密碼上次更換：{formatDate(editingUser.password_changed_at)}
                {editingUser.password_expires_at
                  ? `；到期日：${formatDate(editingUser.password_expires_at)}${editingUser.password_expired ? '（已過期）' : ''}`
                  : '；不到期'}
              </Typography>
            )}

            {editingUser && (
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" color="text.secondary">
                  帳號狀態：
                </Typography>
                <Switch
                  checked={editForm.is_active !== undefined ? editForm.is_active : editingUser.is_active}
                  onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })}
                  disabled={editingUser.id === currentUser?.id}
                />
                <Typography variant="body2">
                  {(editForm.is_active !== undefined ? editForm.is_active : editingUser.is_active) ? '啟用' : '停用'}
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editing}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleEditUser}
            disabled={editing}
            startIcon={editing ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {editing ? '更新中...' : '儲存變更'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 寄送設定密碼信 Dialog (QA #75) */}
      <Dialog
        open={setupMailDialogOpen}
        onClose={() => !sendingSetupMail && setSetupMailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <ForwardToInboxIcon color="secondary" />
            <Typography variant="h6">寄送設定密碼信</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {setupMailError && (
              <Alert severity="error">{setupMailError}</Alert>
            )}

            {setupMailUser && (
              <Alert severity="info">
                將寄一封「設定密碼」信到使用者「{setupMailUser.username}」的信箱 {setupMailUser.email}。
                連結 24 小時內有效、只能使用一次；使用者設定新密碼後，原本的密碼與所有裝置上的登入都會失效。
                管理員不會看到密碼。
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSetupMailDialogOpen(false)} disabled={sendingSetupMail}>
            取消
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSendSetupMail}
            disabled={sendingSetupMail}
            startIcon={sendingSetupMail ? <CircularProgress size={16} /> : <ForwardToInboxIcon />}
          >
            {sendingSetupMail ? '寄送中...' : '確認寄送'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 調整密碼有效期限 Dialog (ADR-049) */}
      <Dialog
        open={policyDialogOpen}
        onClose={() => !policySaving && setPolicyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneIcon color="primary" />
            <Typography variant="h6">調整密碼有效期限</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {policyError && <Alert severity="error">{policyError}</Alert>}
            <Alert severity="warning">
              縮短天數後，密碼已超過新期限的使用者下次登入必須先設定新密碼；不會登出任何人、也不會寄信。
            </Alert>
            <TextField
              fullWidth
              label="密碼有效期限（天）"
              type="number"
              value={policyDays}
              onChange={(e) => {
                setPolicyDays(e.target.value);
                policyFields.clearField('max_age_days');
              }}
              inputProps={{ min: policyRange.min, max: policyRange.max, step: 1, 'data-testid': 'password-max-age-input' }}
              required
              {...policyFields.bind('max_age_days', policyRangeText)}
            />
            <Typography variant="body2" color="text.secondary">
              部署預設：{deploymentDefaultText}
              {policySetting?.source === 'console' ? '（目前生效的是主控台設定）' : '（目前生效）'}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleResetPolicy} disabled={policySaving || policySetting?.source !== 'console'}>
            還原預設
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setPolicyDialogOpen(false)} disabled={policySaving}>
            取消
          </Button>
          <Button
            variant="contained"
            onClick={handleSavePolicy}
            disabled={policySaving}
            startIcon={policySaving ? <CircularProgress size={16} /> : <TuneIcon />}
          >
            {policySaving ? '儲存中...' : '儲存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 解除鎖定 Dialog (ADR-055) */}
      <Dialog
        open={unlockUser !== null}
        onClose={() => !unlocking && setUnlockUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockOpenIcon color="error" />
            <Typography variant="h6">解除鎖定</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {unlockError && <Alert severity="error">{unlockError}</Alert>}
            {unlockUser && (
              <Alert severity="info">
                使用者「{unlockUser.username}」因連續輸錯密碼被暫時鎖定
                {lockedUntilOf(unlockUser) &&
                  `，原本到 ${lockedUntilOf(unlockUser)!.toLocaleTimeString('zh-TW', { hour: '2-digit', minute: '2-digit', hour12: false })} 才會自動解除`}
                。解除後可立即登入。若懷疑有人嘗試猜測密碼，請一併提醒使用者更換密碼。
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setUnlockUser(null)} disabled={unlocking}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleUnlock}
            disabled={unlocking}
            startIcon={unlocking ? <CircularProgress size={16} /> : <LockOpenIcon />}
          >
            {unlocking ? '解除中...' : '確認解除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 要求下次登入更換密碼 Dialog */}
      <Dialog
        open={requireChangeUser !== null}
        onClose={() => !requiringChange && setRequireChangeUser(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockResetIcon color="warning" />
            <Typography variant="h6">要求下次登入更換密碼</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {requireChangeError && <Alert severity="error">{requireChangeError}</Alert>}
            {requireChangeUser && (
              <Alert severity="info">
                使用者「{requireChangeUser.username}」下次登入時，必須先設定新密碼才能繼續使用系統；
                目前已登入的畫面在重新整理後也會轉到密碼修改頁。管理員不會看到或設定密碼。
                如果使用者忘了目前的密碼，請改用「寄送設定密碼信」。
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRequireChangeUser(null)} disabled={requiringChange}>
            取消
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleRequireChange}
            disabled={requiringChange}
            startIcon={requiringChange ? <CircularProgress size={16} /> : <LockResetIcon />}
          >
            {requiringChange ? '設定中...' : '確認'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permanent Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <DeleteForeverIcon color="error" />
            <Typography variant="h6">永久刪除使用者</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            {deleteBlockers && (
              <Alert severity="warning">
                這個帳號還擁有：備份檔案 {deleteBlockers.files ?? 0} 個、File Watcher {deleteBlockers.watchers ?? 0} 台、
                備份目的地 {deleteBlockers.destinations ?? 0} 個、排程 {deleteBlockers.schedules ?? 0} 個。
                請先刪除這些資料後再永久刪除帳號，或保持帳號停用即可。
              </Alert>
            )}
            {deleteUser && (
              <Alert severity="warning">
                即將永久刪除使用者「{deleteUser.username}」（{deleteUser.email}）。此動作無法復原：
                帳號、群組成員資格、登入 session 與告警訂閱都會一併移除；稽核紀錄會保留。
                停用帳號就足以阻止登入，只有確定不再需要這個帳號時才刪除。
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            取消
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteUser}
            disabled={deleting || !!deleteBlockers}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteForeverIcon />}
          >
            {deleting ? '刪除中...' : '確認永久刪除'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
